import { FileText, CheckCircle, AlertTriangle, Shield, Eye } from "lucide-react";
import GenericPracticePage from "@/components/GenericPracticePage";

const ContractReview = () => {
  const mainContent = (
    <>
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-6">Real Estate Contract Review Services in Houston, Texas</h2>
            <p className="text-lg text-muted-foreground mb-8">
              Real estate contracts contain complex legal language that can significantly impact your financial future. A single overlooked clause or unfavorable term can cost you thousands of dollars or trap you in an undesirable agreement. Our experienced real estate attorneys provide comprehensive contract review services to protect your interests, identify potential problems, and negotiate favorable terms before you sign. Whether you're buying, selling, leasing, or investing in property, we ensure you understand exactly what you're agreeing to and help you avoid costly mistakes.
            </p>

            <div className="bg-accent/10 border-l-4 border-accent p-6 rounded-r-lg mb-12">
              <p className="text-lg font-semibold mb-2">
                <AlertTriangle className="inline h-5 w-5 mr-2 text-accent" />
                Don't Sign Without Legal Review
              </p>
              <p className="text-muted-foreground">
                The Texas Real Estate Commission (TREC) forms are legally binding contracts. While these standardized forms are commonly used, they still require careful review and often need addenda to address specific situations. Real estate agents cannot provide legal advice. Having an attorney review your contract before signing protects your investment and ensures your rights are protected.
              </p>
            </div>

            <h3 className="text-3xl font-bold mb-8">What We Review in Your Real Estate Contract</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              <div className="bg-card p-6 rounded-lg border border-accent/20">
                <FileText className="h-8 w-8 text-accent mb-4" />
                <h3 className="text-xl font-bold mb-3">Purchase Price & Financing Terms</h3>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Sales price and payment structure</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Earnest money amount and protection</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Down payment requirements</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Financing contingency terms and deadlines</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Appraisal contingency and gap coverage</span>
                  </li>
                </ul>
              </div>

              <div className="bg-card p-6 rounded-lg border border-accent/20">
                <Eye className="h-8 w-8 text-accent mb-4" />
                <h3 className="text-xl font-bold mb-3">Contingencies & Conditions</h3>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Inspection contingency and option period</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Sale of buyer's existing home contingency</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Survey requirements and objection periods</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Title contingency and cure periods</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>HOA approval (if applicable)</span>
                  </li>
                </ul>
              </div>

              <div className="bg-card p-6 rounded-lg border border-accent/20">
                <Shield className="h-8 w-8 text-accent mb-4" />
                <h3 className="text-xl font-bold mb-3">Seller Disclosures & Property Condition</h3>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Seller's disclosure notice accuracy</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Property condition "as-is" vs repair obligations</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Fixtures and exclusions</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Lead-based paint disclosure (pre-1978 homes)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Environmental hazards and flood zone status</span>
                  </li>
                </ul>
              </div>

              <div className="bg-card p-6 rounded-lg border border-accent/20">
                <AlertTriangle className="h-8 w-8 text-accent mb-4" />
                <h3 className="text-xl font-bold mb-3">Closing Terms & Default Provisions</h3>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Closing date and possession terms</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Prorations (taxes, HOA dues, utilities)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Closing cost allocation</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Default and remedy provisions</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
                    <span>Attorney fees and dispute resolution</span>
                  </li>
                </ul>
              </div>
            </div>

            <h3 className="text-3xl font-bold mb-6">Red Flags We Look For</h3>
            <div className="bg-muted/30 p-8 rounded-lg mb-12">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {[
                  { issue: "Missing Contingencies", risk: "You could lose your earnest money or be forced to buy a defective property" },
                  { issue: "Unreasonable Deadlines", risk: "Insufficient time to conduct proper inspections or secure financing" },
                  { issue: "Vague Property Description", risk: "Disputes over what's included in the sale" },
                  { issue: "One-Sided Default Clauses", risk: "Unfair penalties if you need to terminate the contract" },
                  { issue: "Hidden Costs", risk: "Unexpected fees or expenses not properly disclosed" },
                  { issue: "Improper Earnest Money Terms", risk: "Difficulty recovering your deposit if deal falls through" }
                ].map((item, index) => (
                  <div key={index}>
                    <h4 className="font-bold text-lg mb-2 flex items-center gap-2 text-red-600">
                      <AlertTriangle className="h-5 w-5" />
                      {item.issue}
                    </h4>
                    <p className="text-sm text-muted-foreground pl-7">{item.risk}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center">Our Contract Review Process</h2>
            <div className="space-y-6">
              {[
                { step: "1", title: "Document Submission", desc: "Send us your unsigned contract and any related documents (addenda, disclosures, inspection reports)." },
                { step: "2", title: "Comprehensive Analysis", desc: "We review every clause, identify potential issues, and analyze how each term affects your rights and obligations." },
                { step: "3", title: "Risk Assessment", desc: "Evaluate risks associated with contingencies, deadlines, and default provisions." },
                { step: "4", title: "Consultation & Explanation", desc: "We explain our findings in plain English, highlighting concerns and favorable/unfavorable terms." },
                { step: "5", title: "Negotiation Strategy", desc: "Recommend modifications, addenda, or counteroffers to better protect your interests." },
                { step: "6", title: "Final Review", desc: "After negotiations, we review the final contract before you sign to ensure all agreed changes are properly documented." }
              ].map((item) => (
                <div key={item.step} className="flex gap-4 items-start bg-card p-6 rounded-lg">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-gold-shine text-primary font-bold flex items-center justify-center">
                    {item.step}
                  </div>
                  <div>
                    <h4 className="text-lg font-bold mb-1">{item.title}</h4>
                    <p className="text-muted-foreground">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-12 text-center">Why Professional Contract Review Matters</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center p-6">
                <div className="flex justify-center mb-4">
                  <Shield className="h-12 w-12 text-accent" />
                </div>
                <h3 className="text-xl font-bold mb-2">Protect Your Investment</h3>
                <p className="text-muted-foreground">Avoid costly mistakes that could cost you your home or thousands of dollars</p>
              </div>
              <div className="text-center p-6">
                <div className="flex justify-center mb-4">
                  <Eye className="h-12 w-12 text-accent" />
                </div>
                <h3 className="text-xl font-bold mb-2">Know What You're Signing</h3>
                <p className="text-muted-foreground">Understand every term and your rights and obligations under the contract</p>
              </div>
              <div className="text-center p-6">
                <div className="flex justify-center mb-4">
                  <FileText className="h-12 w-12 text-accent" />
                </div>
                <h3 className="text-xl font-bold mb-2">Negotiate Better Terms</h3>
                <p className="text-muted-foreground">Leverage our expertise to improve contract terms in your favor</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );

  return (
    <GenericPracticePage
      title="Real Estate Contract Review"
      subtitle="Expert Legal Review to Protect Your Property Transaction"
      icon={<FileText className="h-16 w-16" />}
      mainContent={mainContent}
    />
  );
};

export default ContractReview;